import useHomeText from 'hooks/useHomeText';
import { useLayoutEffect, useRef } from 'react';
import AgendaHeader from './AgendaHeader';
import AppointmentItem from './AppointmentItem';
import AppointmentsToPlace from './AppointmentsToPlace';
import { CARD_CLASS } from 'utils/surfaceClasses';

/**
 * « Planning »: the appointments to place, then today's appointments. The card
 * holds in the screen without scrolling: the appointments that do not fit
 * whole are hidden, the button that opens the planning leads to the rest.
 * `onNavigate` gets `planning` or `placeAppointments`.
 */
const AgendaCard = ({ appointments, toPlace, onOpen, onAdd, onNavigate }) => {
  const { text } = useHomeText();
  const list = useRef(null);
  useLayoutEffect(() => {
    const element = list.current;
    if (!element) return undefined;
    const measure = () => {
      const items = [...element.children];
      items.forEach((item) => {
        item.style.display = '';
      });
      const max = element.clientHeight;
      let fitting = 0;
      for (const item of items) {
        if (item.offsetTop - element.offsetTop + item.offsetHeight > max + 1) break;
        fitting += 1;
      }
      items.forEach((item, index) => {
        item.style.display = index < fitting ? '' : 'none';
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [appointments]);

  return (
    <section className={`flex max-h-full min-h-0 flex-col gap-3 px-4 pb-4 pt-3 ${CARD_CLASS}`}>
      <AgendaHeader onOpenPlanning={() => onNavigate('planning')} onAdd={onAdd} />
      {toPlace.total > 0 && (
        <AppointmentsToPlace
          total={toPlace.total}
          beforeTomorrow={toPlace.beforeTomorrow}
          onClick={() => onNavigate('placeAppointments')}
        />
      )}
      {!appointments.length && (
        <p className="text-sm text-gray-400">
          {text(toPlace.total ? 'agenda.empty' : 'agenda.emptyFree')}
        </p>
      )}
      <div ref={list} className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
        {appointments.map((appointment) => (
          <AppointmentItem key={appointment.id} appointment={appointment} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
};

export default AgendaCard;
